import { languageTimeline, type Timeline } from "@veyso/core";
import type { LanguageView, LanguagesView } from "../api.js";
import { isInProgress, switchable } from "../languages/languages.js";

/**
 * The editor's language view, in words and arithmetic: what the bar says,
 * what the export is called, which timeline the preview plays, and which of
 * the language's words may be edited. Kept apart from the components so the
 * tests at the root can run them without React.
 */

/**
 * What the bar under the header says while a language is shown: a bold lead
 * naming the language, then the rest, and the two joined.
 */
export function languageBar(language: Pick<LanguageView, "name" | "nativeName" | "outOfDate">): {
  lead: string;
  rest: string;
  text: string;
} {
  const { name, nativeName } = language;
  const lead = `Viewing ${nativeName}.`;
  const shared = `Edit the ${name} words here. Cuts, timing and zooms are shared with English. Change them in EN, and ${nativeName} offers an update.`;
  const rest = language.outOfDate
    ? `${shared} The English changed after ${nativeName} was made. Update it to match.`
    : shared;
  return { lead, rest, text: `${lead} ${rest}` };
}

/**
 * Whether a selected language stays shown after a fresh read. Only one that
 * is gone (discarded) or failed lets go: approving or revoicing it keeps the
 * customer where they are, watching it be voiced.
 */
export function keepsSelection(view: Pick<LanguagesView, "languages">, code: string): boolean {
  const language = view.languages.find((one) => one.code === code);
  return language !== undefined && language.status !== "failed";
}

/**
 * The languages the switch offers: those that can be looked at, and those
 * being made — a language appears the moment it is added, with its progress,
 * rather than only once it is done. In the view's order.
 */
export function switchOptions(view: Pick<LanguagesView, "languages"> | null, current: string | null): LanguageView[] {
  const offered = new Set(switchable(view).map((one) => one.code));
  return (view?.languages ?? []).filter(
    (one) => offered.has(one.code) || isInProgress(one.status) || one.code === current,
  );
}

/** The export button's name: the shown language's, or plain "Export" for the English. */
export function exportLabel(language: Pick<LanguageView, "nativeName"> | null): string {
  return language ? `Export ${language.nativeName}` : "Export";
}

/**
 * What the preview plays: the English timeline as it is, or the same picture
 * with the language's voice, subtitles and — for the whole video — its words
 * on screen. The English object itself for English, so nothing downstream
 * re-renders for a copy.
 */
export function previewTimeline(
  timeline: Timeline,
  language: (Pick<LanguageView, "code" | "depth" | "lines" | "texts"> & Partial<Pick<LanguageView, "cues">>) | null,
): Timeline {
  if (!language) return timeline;
  return languageTimeline(timeline, {
    code: language.code,
    depth: language.depth,
    lines: language.lines,
    texts: language.texts,
    // The server's subtitles: timed word by word from the voice, so they highlight as it speaks.
    ...(language.cues ? { cues: language.cues } : {}),
  });
}

/**
 * Whether the words on screen may be edited: a whole-video language made from
 * today's English, in review — or, for a product demo, ready too. A launch
 * film's words are drawn into its picture when it is voiced.
 */
export function textsEditable(language: Pick<LanguageView, "status" | "depth" | "outOfDate">, launch = false): boolean {
  return (
    !language.outOfDate &&
    language.depth === "video" &&
    (language.status === "review" || (language.status === "ready" && !launch))
  );
}

/** How many edited lines a free revoice would speak; none unless the language is ready and up to date. */
export function revoiceCount(language: Pick<LanguageView, "status" | "outOfDate" | "lines">): number {
  if (language.status !== "ready" || language.outOfDate) return 0;
  return language.lines.filter((line) => line.edited).length;
}

/** A piece of screen text's length as its limit counts it: characters, as code points. */
export const textLength = (text: string): number => [...text].length;
